import { useMemo, useState } from 'react'
import { ChevronRight, PackageOpen, Ticket, TicketCheck } from 'lucide-react'
import { Link } from 'react-router-dom'
import { collection, query, where } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { useAuth } from '@/lib/auth'
import { useQuerySnapshot } from '@/hooks/useQuerySnapshot'
import { Badge, Button, Card, EmptyState, SectionTitle, Spinner } from '@/components/ui'
import { ActivateCodeForm } from '@/components/ActivateCodeForm'
import { RegisterSlipsModal } from '@/components/RegisterSlipsModal'
import { useFeature } from '@/lib/features'
import { useBooklets } from '@/hooks/useBooklets'
import { useMyCodes } from '@/hooks/useMyCodes'
import { formatCode } from '@shared/codes'
import { BOOKLET_STATUS_LABELS, COLLECTIONS, type Booklet, type Code } from '@shared/model'
import { formatDate, formatDateTime } from '@/lib/format'
import { QueryError } from '@/components/QueryError'

export function MyCodesPage() {
  const { booklets, loading: bookletsLoading, error: bookletsError } = useBooklets()
  const { codes, loading: codesLoading, error: codesError } = useMyCodes()

  const bookletById = useMemo(() => {
    const map: Record<string, Booklet> = {}
    for (const booklet of booklets) map[booklet.id] = booklet
    return map
  }, [booklets])

  const sorted = useMemo(
    () =>
      [...codes].sort((a, b) => {
        const first = bookletById[a.bookletId]?.startDate ?? ''
        const second = bookletById[b.bookletId]?.startDate ?? ''
        return second.localeCompare(first)
      }),
    [codes, bookletById],
  )

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-extrabold">Mina koder</h1>
        <p className="text-sm text-muted">
          Här ligger alla häften du aktiverat. Köper du fler kan du aktivera dem på samma konto.
        </p>
      </div>

      {/*
        * Ingången till försäljningsvyn ligger här och inte i huvudnavigationen.
        * De allra flesta som använder appen är köpare med ett enda häfte; att
        * ge dem en femte flik för något de aldrig kommer röra vore att göra
        * navigeringen sämre för de många.
        */}
      <HeldBookletsLink />

      <Card className="p-5">
        <SectionTitle>Aktivera ett häfte</SectionTitle>
        <ActivateCodeForm />
      </Card>

      <section>
        <SectionTitle>Aktiverade häften</SectionTitle>

        {bookletsError || codesError ? (
          <QueryError error={bookletsError ?? codesError} />
        ) : bookletsLoading || codesLoading ? (
          <Spinner />
        ) : sorted.length === 0 ? (
          <EmptyState
            icon={<Ticket size={44} aria-hidden />}
            title="Inga häften än"
            description="Aktivera koden från lappen du köpte så dyker häftet upp här."
          />
        ) : (
          <ul className="space-y-2.5">
            {sorted.map((code) => {
              const booklet = bookletById[code.bookletId]
              const upcoming = booklet?.status === 'upcoming'
              const expired = booklet?.status === 'expired'

              return (
                <li key={code.id}>
                  <Card className="flex flex-wrap items-center justify-between gap-3 p-4">
                    <div className="min-w-0">
                      <p className="font-display font-extrabold text-ink">
                        {booklet?.periodName ?? 'Okänd period'}
                      </p>
                      <p className="font-mono text-sm tracking-widest text-muted">
                        {formatCode(code.id)}
                      </p>
                      <p className="mt-1 text-xs text-faint">
                        Aktiverad {formatDateTime(code.activatedAt)}
                      </p>
                    </div>

                    <div className="flex flex-col items-end gap-1.5">
                      <Badge tone={expired ? 'neutral' : upcoming ? 'brand' : 'success'}>
                        {booklet ? BOOKLET_STATUS_LABELS[booklet.status] : '–'}
                      </Badge>
                      {upcoming && booklet && (
                        <span className="text-xs font-semibold text-brand-ink">
                          Aktiveras {formatDate(booklet.startDate)}
                        </span>
                      )}
                      {expired && booklet && (
                        <span className="text-xs text-faint">
                          Gick ut {formatDate(booklet.endDate)}
                        </span>
                      )}
                    </div>
                  </Card>
                </li>
              )
            })}
          </ul>
        )}
      </section>
    </div>
  )
}

/**
 * Ingången för den som ska sälja häften.
 *
 * Två lägen, och det andra är det som saknades. Rutan visades tidigare bara
 * för den som redan höll häften, vilket stämde när alla häften kom via en
 * betald beställning: koderna landade på kontot, och då dök länken upp.
 *
 * Med fysiska lappar börjar föräldern på noll. Hen har en bunt papper i
 * handen och ska registrera dem – och just då fanns ingen väg alls till
 * registreringen, eftersom villkoret för att visa vägen var att man redan
 * gått den. Håller man inget erbjuds därför registreringen direkt här,
 * i stället för en länk till en tom sida.
 */
function HeldBookletsLink() {
  const { user } = useAuth()
  const [registering, setRegistering] = useState(false)
  const slipsOn = useFeature('slipRegistration')

  const heldQuery = useMemo(
    () =>
      user ? query(collection(db, COLLECTIONS.codes), where('holderUid', '==', user.uid)) : null,
    [user],
  )
  const { data: held, loading } = useQuerySnapshot<Code>(heldQuery)

  if (held.length === 0) {
    // Inget medan frågan är obesvarad: rutan ska inte blinka förbi för de
    // många som bara är köpare.
    if (loading) return null
    // Är registreringen avstängd finns ingenting att erbjuda den som inte
    // redan håller häften. Den som gör det ser sin länk som vanligt.
    if (!slipsOn) return null

    return (
      <>
        <Card className="flex flex-wrap items-center gap-3 p-4">
          <PackageOpen size={22} className="shrink-0 text-brand-ink" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="font-semibold">Har du fått lappar att sälja?</p>
            <p className="text-sm text-muted">
              Registrera koderna så samlas häftena här.
            </p>
          </div>
          <Button size="sm" icon={<TicketCheck size={15} />} onClick={() => setRegistering(true)}>
            Registrera
          </Button>
        </Card>

        {registering && (
          <RegisterSlipsModal mode="self" onClose={() => setRegistering(false)} />
        )}
      </>
    )
  }

  const handedOut = held.filter((code) => code.handedOutAt || code.status === 'activated').length

  return (
    <Link
      to="/mina-haften"
      className="flex items-center gap-3 rounded-card border border-border bg-surface p-4 shadow-card transition-colors hover:border-border-strong"
    >
      <PackageOpen size={22} className="shrink-0 text-brand-ink" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">Häften du ska sälja</p>
        <p className="text-sm text-muted">
          {handedOut} av {held.length} utdelade
        </p>
      </div>
      <ChevronRight size={18} className="shrink-0 text-faint" aria-hidden />
    </Link>
  )
}
